import React from 'react';
import { Box, ButtonBase, IconButton, Tooltip, Typography } from '@mui/material';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { useFontScale } from '../contexts/FontScaleContext';
import {
  BRAND_GRADIENT,
  RULE_GRADIENT,
  G1,
  TINT,
  controlShellSx,
  keyframe,
  reduceMotion,
} from '../theme/oceanTokens';

/** Round stepper button: ghost at rest, fills with the brand gradient on hover. */
const stepSx = {
  width: 26,
  height: 26,
  p: 0,
  borderRadius: '50%',
  color: G1,
  transition:
    'transform .22s cubic-bezier(.2,.8,.3,1), background .25s, box-shadow .25s, color .2s',
  '&:hover': {
    background: BRAND_GRADIENT,
    backgroundSize: '200% 100%',
    color: '#fff',
    transform: 'scale(1.12)',
    boxShadow: '0 4px 12px rgb(var(--ocean-accent-rgb) / 0.3)',
  },
  '&:active': { transform: 'scale(0.94)' },
  '&.Mui-disabled': {
    color: 'rgb(var(--ocean-accent-rgb) / 0.32)',
    background: 'none',
    boxShadow: 'none',
  },
  [reduceMotion]: { transition: 'none', '&:hover': { transform: 'none' } },
};

/** Gradient-filled text, used for the "Aa" glyph and the percentage. */
const gradientInk = {
  background: BRAND_GRADIENT,
  backgroundSize: '240% 100%',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  color: 'transparent',
};

/**
 * Text-size stepper for the app bar.
 *
 * The "Aa" in the middle is rendered at the size it is setting, so the control
 * previews its own effect; a gradient track underneath shows where the current
 * value sits in the allowed range. Clicking the middle resets to 100%.
 */
const FontSizeControl = ({ compact = false }) => {
  const { increase, decrease, reset, canIncrease, canDecrease, percent, scale, progress } =
    useFontScale();

  // Kept inside a fixed-width box so the app bar never reflows as it grows.
  const glyphPx = 10 + progress * 6;

  return (
    <Box
      role="group"
      aria-label="Text size controls"
      sx={{ ...controlShellSx, gap: 0.25, px: 0.5, py: 0.25 }}
    >
      <Tooltip title="Decrease text size">
        <span>
          <IconButton
            size="small"
            onClick={decrease}
            disabled={!canDecrease}
            aria-label="Decrease text size"
            sx={stepSx}
          >
            <RemoveRoundedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title={scale === 1 ? 'Text size 100%' : `Text size ${percent}% — click to reset`}>
        <ButtonBase
          onClick={reset}
          aria-label={`Current text size ${percent}%. Click to reset.`}
          sx={{
            flexDirection: 'column',
            gap: 0.25,
            px: 0.75,
            py: 0.25,
            borderRadius: '9px',
            transition: 'background-color .2s',
            '&:hover': { backgroundColor: 'rgb(var(--ocean-accent-rgb) / 0.07)' },
            '&:focus-visible': { outline: '2px solid #0C4A6E', outlineOffset: 2 },
            [reduceMotion]: { transition: 'none' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
            {/* Previews the setting: the glyph is drawn at the chosen size. */}
            <Box
              sx={{
                width: 22,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'baseline',
              }}
            >
              <Box
                component="span"
                sx={{
                  ...gradientInk,
                  fontWeight: 800,
                  lineHeight: 1,
                  fontSize: `${glyphPx}px`,
                  transition: 'font-size .28s cubic-bezier(.2,.8,.3,1)',
                  [reduceMotion]: { transition: 'none' },
                }}
              >
                Aa
              </Box>
            </Box>
            {!compact && (
              <Typography
                // Remounts on change so the pop animation replays each step.
                key={percent}
                sx={{
                  ...gradientInk,
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  lineHeight: 1,
                  minWidth: 30,
                  textAlign: 'center',
                  fontVariantNumeric: 'tabular-nums',
                  animation: `${keyframe.pop} .28s cubic-bezier(.2,1.4,.4,1)`,
                  [reduceMotion]: { animation: 'none' },
                }}
              >
                {percent}%
              </Typography>
            )}
          </Box>

          {!compact && (
            <Box
              sx={{
                width: '100%',
                height: 3,
                borderRadius: 99,
                bgcolor: TINT.track,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  width: `${Math.round(progress * 100)}%`,
                  borderRadius: 99,
                  background: RULE_GRADIENT,
                  transition: 'width .3s cubic-bezier(.2,.8,.3,1)',
                  [reduceMotion]: { transition: 'none' },
                }}
              />
            </Box>
          )}
        </ButtonBase>
      </Tooltip>

      <Tooltip title="Increase text size">
        <span>
          <IconButton
            size="small"
            onClick={increase}
            disabled={!canIncrease}
            aria-label="Increase text size"
            sx={stepSx}
          >
            <AddRoundedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

export default FontSizeControl;
